import { styled, alpha } from "@mui/material/styles";
import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import HelpOutlineSharpIcon from "@mui/icons-material/HelpOutlineSharp";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import ScienceIcon from "@mui/icons-material/Science";
import SchoolIcon from "@mui/icons-material/School";
import { MouseEvent, useState, useRef, useEffect } from "react";
import useStore from "../../../Store/Store";
import { createPortal } from "react-dom";

type VideoTutorialSection = "" | "GETTING_STARTED" | "SIMULATION" | "DOCS";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  zIndex: 10000000, // Extremely high z-index to appear on top of everything
  "& .MuiPaper-root": {
    borderRadius: 15,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.custom.lightGrey,
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.secondary.main,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

export default function HelpMenu() {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const iconRef = useRef<HTMLDivElement>(null);
  const [iconPosition, setIconPosition] = useState({ top: 0, right: 0 });

  const {
    isRunningSimulation,
    selectedSimulationKey,
    isMaxSD,
    showLandingComponent,
  } = useStore((state) => ({
    isRunningSimulation: state.isRunningSimulation,
    selectedSimulationKey: state.simulationsState.selectedSimulationKey,
    isMaxSD: state.isMaxSD,
    showLandingComponent: state.showLandingComponent,
  }));

  // Hide all buttons except help when SD is maximized AND running simulation
  const shouldHideButton = false; // Help icon is never hidden unless landing page is showing

  // Apply smaller styling when SD is maximized
  const shouldApplySmallStyling = isMaxSD;

  const {
    setDialog,
    setVideoTutorialSection,
    setSelectedTab,
    setIsSimulationOnboardingActive,
  } = useStore((state) => ({
    setDialog: state.setDialog,
    setVideoTutorialSection: state.setVideoTutorialSection,
    setSelectedTab: state.setSelectedTab,
    setIsSimulationOnboardingActive: state.setIsSimulationOnboardingActive,
  }));

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const showTutorial = (section: string) => {
    if (section === "general") {
      // First check if we have simulations, if not start regular tutorial
      const simulations = useStore.getState().simulationsState.simulations;

      if (Object.keys(simulations).length > 0) {
        // Start the guided tutorial with simulation onboarding steps immediately
        // Close the simulation and move to simulations tab
        useStore.getState().setSelectedSimulationKey("");
        // Also ensure we're on the simulations tab to see the tutorial
        setSelectedTab(3);
        // delay for 0.5 seconds before starting the tutorial
        // to allow the UI to change
        setTimeout(() => {
          setIsSimulationOnboardingActive(true);
        }, 500);
      } else {
        // Start regular tutorial if no simulations exist
        useStore.getState().setStartDemo(true);
        setSelectedTab(0);
      }
    } else if (section === "simulation") {
      setDialog("SIMULATION_TUTORIAL_DIALOG");
    }
    handleClose();
  };

  const showVideoTutorial = (section: VideoTutorialSection) => {
    setVideoTutorialSection(section);
    setDialog("VIDEO_TUTORIAL_DIALOG");
    handleClose();
  };

  const showDocumentation = () => {
    const newTab = window.open(
      "https://www.notion.so/Code-Canvas-Public-Docs-d1f92936b4704b50a36f5abac13e72bf",
      "_blank"
    );
    if (newTab) {
      newTab.focus();
    }
    handleClose();
  };

  // Update icon position when component mounts or dependency changes
  useEffect(() => {
    const updatePosition = () => {
      if (iconRef.current) {
        const rect = iconRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;

        setIconPosition({
          top: rect.top,
          right: viewportWidth - rect.right,
        });
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [shouldApplySmallStyling]);

  return (
    <>
      {/* Placeholder div to maintain position in Header */}
      <div
        ref={iconRef}
        style={{
          display: "flex",
          alignItems: "center",
          height: "28px",
        }}
      >
        <div style={{ width: "24px", height: "24px" }} />
      </div>

      {/* Portal renders the actual icon outside of Header's DOM hierarchy */}
      {!showLandingComponent &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: shouldApplySmallStyling
                ? `${iconPosition.top - 5}px`
                : `${iconPosition.top}px`,
              right: shouldApplySmallStyling
                ? "15px"
                : `${iconPosition.right}px`,
              zIndex: 9999999,
              display: "flex",
              alignItems: "center",
              height: shouldApplySmallStyling ? "20px" : "28px",
              transition: "all 0.2s ease",
            }}
          >
            <div
              onClick={handleClick}
              data-help-icon="true"
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: shouldApplySmallStyling ? "22px" : "24px",
                height: shouldApplySmallStyling ? "22px" : "24px",
                justifyContent: "center",
                transition: "all 0.2s ease",
              }}
            >
              <HelpOutlineSharpIcon
                className="HelpIcon"
                color="primary"
                sx={{
                  fontSize: shouldApplySmallStyling ? "22px" : "24px",
                  transition: "font-size 0.2s ease",
                }}
              />
            </div>
          </div>,
          document.body
        )}

      <StyledMenu
        id="HelpMenu"
        MenuListProps={{
          "aria-labelledby": "help-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={() => showTutorial("general")} disableRipple>
          <SchoolIcon />
          Guided Tutorial
        </MenuItem>
        <MenuItem onClick={() => showTutorial("simulation")} disableRipple>
          <ScienceIcon />
          Simulation Tutorial
        </MenuItem>
        <MenuItem
          onClick={() => showVideoTutorial("GETTING_STARTED")}
          disableRipple
        >
          <OndemandVideoIcon />
          Manual diagram building tutorial
        </MenuItem>
        <MenuItem onClick={showDocumentation} disableRipple>
          <MenuBookIcon />
          Documentation
        </MenuItem>
      </StyledMenu>
    </>
  );
}
